import { useHistory } from 'react-router-dom'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { AlmanacFile, AlmanacFileSummary, ArticleView } from 'types'
import { createNewArticle } from 'utils'
import { reportError } from 'utils/error'
import { getArticlePath } from 'utils/urlUtils'
import QueryString from 'qs'
import { ARTICLE_VIEW_PARAM } from 'components/ArticleViewSelect/types'
import { useQueryParams } from 'hooks/useQueryParams'
import { useRootStore } from 'stores/RootStore'
import { useState } from 'react'

export function useCreateDoc({
  chosenWorkspaceId,
  chosenFolder,
  docOrFolderName = 'Untitled',
  emoji,
  handleClose,
  onSuccess,
}: {
  chosenWorkspaceId?: number
  chosenFolder?: Pick<AlmanacFile, 'project_id' | 'handbook'> | null
  docOrFolderName?: string
  emoji?: string
  handleClose?: () => void
  onSuccess?: (data: { almanac_file: AlmanacFile | Omit<AlmanacFileSummary, 'ancestors'> }) => void
}) {
  const history = useHistory()
  const workspaces = useWorkspaces()
  const { focusView } = useQueryParams()
  const handbooksStore = useRootStore().handbooks
  const [isLoading, setIsLoading] = useState(false)

  const findWorkspace = (id: number | undefined) => {
    return workspaces.find((ws) => ws.id === id)
  }

  const createDoc = async () => {
    setIsLoading(true)
    const chosenWorkspace = findWorkspace(chosenWorkspaceId)

    if (!chosenWorkspace) return
    const response = await createNewArticle({
      workspaceKey: chosenWorkspace.key,
      projectId: chosenFolder?.project_id,
      title: docOrFolderName,
      header_emoji: emoji,
    })

    if (response.type === SUCCESS_RESPONSE_TYPE) {
      handleClose?.()

      if (onSuccess) {
        onSuccess({ almanac_file: response.data.article.almanac_file })
      } else {
        history.push({
          pathname: getArticlePath(response.data.article),
          search: QueryString.stringify(
            {
              focusView,
              [ARTICLE_VIEW_PARAM]: ArticleView.Editing,
            },
            { filter: (_, value) => value ?? undefined },
          ),
        })
      }

      handbooksStore.refresh(chosenFolder?.handbook?.key)

      setIsLoading(false)
      return response.data.article
    } else {
      reportError(new Error('Error while creating a new document'), { metaData: { response } })
      showErrorMessageToast('Something went wrong 😢')
    }
  }

  return { createDoc, isLoading }
}
